import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Check, X, Clock, AlertCircle, Users, Search, TrendingUp, PieChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export default function LeaveManagement() {
  const [applications, setApplications] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    days_count: 0
  });
  
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');
  const isHRAdmin = ['admin', 'hr_manager'].includes(user.role);
  const canReview = ['admin', 'hr_manager', 'department_head', 'manager'].includes(user.role);
  const isEmployee = user.role === 'employee';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('hrToken') || localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [appsRes, typesRes, holidaysRes, employeesRes] = await Promise.all([
        axios.get(`${API}/leave/applications`, { headers }),
        axios.get(`${API}/leave/types`, { headers }),
        axios.get(`${API}/public-holidays?year=2025`, { headers }),
        isHRAdmin ? axios.get(`${API}/employees`, { headers }) : Promise.resolve({ data: [] })
      ]);
      
      // Filter applications based on user role
      if (isEmployee) {
        setApplications(appsRes.data.filter(app => app.employee_id === user.employee_id));
      } else {
        setApplications(appsRes.data);
      }
      
      setLeaveTypes(typesRes.data);
      setPublicHolidays(holidaysRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate currently on leave (approved leaves that include today)
  const currentlyOnLeave = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return applications.filter(app => {
      if (app.status !== 'approved') return false;
      return app.start_date <= today && app.end_date >= today;
    });
  }, [applications]);

  // Leave analytics by category
  const leaveAnalytics = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const analytics = {};
    
    leaveTypes.forEach(type => {
      const categoryLeaves = applications.filter(app => {
        if (app.leave_type_id !== type.leave_type_id) return false;
        if (app.status !== 'approved') return false;
        return app.start_date <= today && app.end_date >= today;
      });
      
      analytics[type.leave_type_id] = {
        ...type,
        count: categoryLeaves.length,
        employees: categoryLeaves
      };
    });
    
    return analytics;
  }, [applications, leaveTypes]);

  // Filter employees on leave for selected category
  const filteredEmployeesOnLeave = useMemo(() => {
    if (!selectedCategory) return currentlyOnLeave;
    
    let filtered = leaveAnalytics[selectedCategory]?.employees || [];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app => {
        const employee = employees.find(e => e.employee_id === app.employee_id);
        return (
          app.employee_name?.toLowerCase().includes(query) ||
          app.employee_id?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [selectedCategory, leaveAnalytics, currentlyOnLeave, searchQuery, employees]);

  // Calculate business days excluding weekends and public holidays
  const calculateBusinessDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) return 0;
    
    let count = 0;
    const holidayDates = publicHolidays.map(h => h.date);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dayOfWeek = date.getDay();
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Skip public holidays
        if (!holidayDates.includes(dateStr)) {
          count++;
        }
      }
    }
    
    return count;
  };

  // Update days count when dates change
  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      const days = calculateBusinessDays(formData.start_date, formData.end_date);
      setFormData(prev => ({ ...prev, days_count: days }));
    }
  }, [formData.start_date, formData.end_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.days_count === 0) {
      toast.error('Please select valid dates (excluding weekends and holidays)');
      return;
    }
    
    try {
      await axios.post(`${API}/leave/applications`, formData);
      toast.success('Leave application submitted successfully');
      setDialogOpen(false);
      setFormData({
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleReview = async (leaveId, action) => {
    try {
      await axios.put(`${API}/leave/applications/${leaveId}/review`, { action });
      toast.success(`Leave ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to review leave');
    }
  };

  const filteredApplications = applications.filter(app => {
    if (selectedTab === 'all') return true;
    return app.status === selectedTab;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
      approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  // HR Admin Analytics View
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Leave Analytics</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Monitor staff leave status and patterns</p>
          </div>
        </div>

        {/* Currently On Leave Summary */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Users className="h-6 w-6" />
                <span className="text-blue-100 font-medium">Currently On Leave</span>
              </div>
              <div className="text-4xl font-bold">{currentlyOnLeave.length}</div>
              <p className="text-blue-100 text-sm mt-1">staff members today</p>
            </div>
            <div className="text-right">
              <PieChart className="h-16 w-16 text-blue-200 opacity-50" />
            </div>
          </div>
        </div>

        {/* Leave Categories Analytics */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Leave by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {leaveTypes.map(type => {
              const analytics = leaveAnalytics[type.leave_type_id];
              const isSelected = selectedCategory === type.leave_type_id;
              
              return (
                <button
                  key={type.leave_type_id}
                  onClick={() => setSelectedCategory(isSelected ? null : type.leave_type_id)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${type.color}20` }}
                  >
                    <CalendarIcon className="h-5 w-5" style={{ color: type.color }} />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{type.name}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{analytics?.count || 0}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">on leave today</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Employee List */}
        {(selectedCategory || currentlyOnLeave.length > 0) && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {selectedCategory 
                  ? `${leaveTypes.find(t => t.leave_type_id === selectedCategory)?.name} - Employees on Leave`
                  : 'All Employees Currently on Leave'
                }
              </h3>
              <button 
                onClick={() => setSelectedCategory(null)}
                className={`text-sm text-blue-600 hover:text-blue-700 ${!selectedCategory && 'hidden'}`}
              >
                Show All
              </button>
            </div>
            
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by name, employee ID, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>

            {/* Employee List */}
            {filteredEmployeesOnLeave.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400">
                  {searchQuery ? 'No employees found matching your search' : 'No employees on leave in this category'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Leave Type</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Duration</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Days</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Return Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredEmployeesOnLeave.map(app => {
                      const leaveType = leaveTypes.find(lt => lt.leave_type_id === app.leave_type_id);
                      const employee = employees.find(e => e.employee_id === app.employee_id);
                      const returnDate = new Date(app.end_date);
                      returnDate.setDate(returnDate.getDate() + 1);
                      
                      return (
                        <tr key={app.leave_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {(app.employee_name || 'U').charAt(0)}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{app.employee_name || 'N/A'}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{app.employee_id}</p>
                                {employee?.email && (
                                  <p className="text-xs text-slate-400 dark:text-slate-500">{employee.email}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span 
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                              style={{ backgroundColor: `${leaveType?.color}20`, color: leaveType?.color }}
                            >
                              {leaveType?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                            {new Date(app.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} - {new Date(app.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{app.days_count} days</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                            {returnDate.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pending Applications for Review */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Pending Leave Applications</h3>
            <span className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-3 py-1 rounded-full text-sm font-medium">
              {applications.filter(a => a.status === 'pending').length} pending
            </span>
          </div>
          
          {applications.filter(a => a.status === 'pending').length === 0 ? (
            <div className="text-center py-8">
              <Check className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">No pending applications to review</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Leave Type</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Duration</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Reason</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {applications.filter(a => a.status === 'pending').map(app => {
                    const leaveType = leaveTypes.find(lt => lt.leave_type_id === app.leave_type_id);
                    return (
                      <tr key={app.leave_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900 dark:text-slate-100">{app.employee_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{app.employee_id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${leaveType?.color}20`, color: leaveType?.color }}
                          >
                            {leaveType?.name || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          {new Date(app.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} - {new Date(app.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}
                          <span className="ml-2 text-slate-500">({app.days_count} days)</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                          {app.reason || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              size="sm"
                              onClick={() => handleReview(app.leave_id, 'approve')}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8"
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReview(app.leave_id, 'reject')}
                              className="h-8"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Employee/Manager View (Original)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Leave Management</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">{applications.length} total applications</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600">
              <Plus className="h-4 w-4 mr-2" />
              Apply Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Leave Type *</Label>
                <select
                  required
                  value={formData.leave_type_id}
                  onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="">Select leave type</option>
                  {leaveTypes.map(type => (
                    <option key={type.leave_type_id} value={type.leave_type_id}>
                      {type.name} ({type.days_per_year} days/year)
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="dark:bg-slate-900 dark:border-slate-600"
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    required
                    value={formData.end_date}
                    min={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="dark:bg-slate-900 dark:border-slate-600"
                  />
                </div>
              </div>

              {formData.start_date && formData.end_date && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm text-blue-900 dark:text-blue-300 font-medium">
                      Total: {formData.days_count} business day{formData.days_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                    (Excluding weekends and public holidays)
                  </p>
                </div>
              )}

              <div>
                <Label>Reason *</Label>
                <Textarea
                  required
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Provide reason for leave..."
                  className="dark:bg-slate-900 dark:border-slate-600"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                  Submit Application
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-slate-200 dark:border-slate-700">
        {['all', 'pending', 'approved', 'rejected'].map(tab => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              selectedTab === tab
                ? 'text-slate-900 dark:text-slate-100 border-b-2 border-slate-900 dark:border-slate-100'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {tab} ({applications.filter(a => tab === 'all' ? true : a.status === tab).length})
          </button>
        ))}
      </div>

      {/* Applications List */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
        {filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">No leave applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Employee</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Leave Type</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Duration</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Days</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Status</th>
                  {canReview && <th className="text-center px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Action</th>}
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map(app => {
                  const leaveType = leaveTypes.find(lt => lt.leave_type_id === app.leave_type_id);
                  return (
                    <tr 
                      key={app.leave_id} 
                      className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                      onClick={() => {
                        toast.info(`Leave Application: ${app.employee_name || 'Employee'}\n${leaveType?.name || 'Leave'}: ${app.days_count} days\nStatus: ${app.status}\nReason: ${app.reason || 'N/A'}`);
                      }}
                      title="Click to view details"
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{app.employee_name || 'N/A'}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{app.employee_id}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${leaveType?.color}20`, color: leaveType?.color }}>
                          {leaveType?.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">
                        {new Date(app.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} - {new Date(app.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {app.days_count} days
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(app.status)}`}>
                          {app.status}
                        </span>
                      </td>
                      {canReview && (
                        <td className="px-6 py-4 text-center">
                          {app.status === 'pending' && (
                            <div className="flex items-center justify-center space-x-2">
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleReview(app.leave_id, 'approve'); }}
                                className="bg-emerald-600 hover:bg-emerald-700 h-8"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={(e) => { e.stopPropagation(); handleReview(app.leave_id, 'reject'); }}
                                className="h-8"
                              >
                                <X className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
